/**
 * CheckoutDetailPage - Ecran de check-out detaille
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 5.5, 5.6, 6.3, 6.4
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  EyeIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { checkoutService, generateIdempotencyKey } from '../services/checkoutService';
import { CheckoutDetail, ValidationCheck, ValidationResult } from '../types/checkout';

const formatCurrency = (amount: number | undefined, currency?: string): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ACCEPTED_FORMATS = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const DEPARTURE_MODES = [
  { value: 'normal', label: 'Départ normal' },
  { value: 'early', label: 'Départ anticipé' },
  { value: 'late', label: 'Départ tardif' },
  { value: 'transfer', label: 'Transfert' },
  { value: 'no_show', label: 'No-show' },
  { value: 'other', label: 'Autre' },
];

export default function CheckoutDetailPage() {
  const { stayId } = useParams<{ stayId: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [checkoutDetail, setCheckoutDetail] = useState<CheckoutDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [paymentCurrency, setPaymentCurrency] = useState<'USD' | 'CDF'>('USD');

  // Editable departure fields
  const [departureDate, setDepartureDate] = useState('');
  const [departureMode, setDepartureMode] = useState('normal');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [updatingDeparture, setUpdatingDeparture] = useState(false);

  const loadCheckoutDetail = useCallback(async () => {
    if (!stayId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await checkoutService.getCheckoutDetail(stayId);
      setCheckoutDetail(data);
      const loadedCurrency = String(data.billing?.currency || 'USD').toUpperCase();
      setPaymentCurrency(loadedCurrency === 'CDF' ? 'CDF' : 'USD');
      // Pre-fill departure fields from loaded data, fallback to today
      if (data.stay?.checkoutAt && data.stay.checkoutAt !== '') {
        const parsed = new Date(data.stay.checkoutAt);
        setDepartureDate(isNaN(parsed.getTime()) ? new Date().toISOString().split('T')[0] : parsed.toISOString().split('T')[0]);
      } else {
        setDepartureDate(new Date().toISOString().split('T')[0]);
      }
    } catch (err: any) {
      console.error('Error loading checkout detail:', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message;
      setError(typeof msg === 'string' ? msg : 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [stayId]);

  useEffect(() => {
    loadCheckoutDetail();
  }, [loadCheckoutDetail]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !stayId) return;
    if (!ACCEPTED_FORMATS.includes(file.type)) {
      setUploadError('Format non accepté. Formats acceptés: PDF, JPG, JPEG, PNG');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Fichier trop volumineux. Taille maximale: 10 Mo');
      return;
    }
    try {
      setUploading(true);
      setUploadError(null);
      const response = await checkoutService.uploadInvoice(stayId, file);
      setCheckoutDetail((prev) =>
        prev ? { ...prev, invoice: { fileUrl: response.fileUrl, fileName: response.fileName, uploadedAt: response.uploadedAt } } : null
      );
    } catch (err: any) {
      console.error('Error uploading invoice:', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message;
      setUploadError(typeof msg === 'string' ? msg : 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpdateDeparture = async () => {
    if (!stayId || !departureDate) return;
    try {
      setUpdatingDeparture(true);
      const data = await checkoutService.updateDeparture(stayId, {
        checkoutAt: departureDate,
        departureMode,
        invoiceAmount: invoiceAmount ? parseFloat(invoiceAmount) : undefined,
      });
      setCheckoutDetail(data);
      // Sync local departure date state from response
      if (data.stay?.checkoutAt && data.stay.checkoutAt !== '') {
        const parsed = new Date(data.stay.checkoutAt);
        if (!isNaN(parsed.getTime())) {
          setDepartureDate(parsed.toISOString().split('T')[0]);
        }
      }
    } catch (err: any) {
      console.error('Error updating departure:', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Erreur lors de la mise à jour';
      setError(typeof msg === 'string' ? msg : 'Erreur');
    } finally {
      setUpdatingDeparture(false);
    }
  };

  const handleValidate = async () => {
    if (!stayId) return;
    try {
      setValidating(true);
      const result = await checkoutService.validateCheckout(stayId);
      setValidationResult(result);
    } catch (err: any) {
      console.error('Error validating checkout:', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message;
      setError(typeof msg === 'string' ? msg : 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  const handleConfirmCheckout = async () => {
    if (!stayId) return;
    try {
      setConfirming(true);
      const idempotencyKey = generateIdempotencyKey();
      const paymentMethod = checkoutDetail?.payment?.method || 'CASH';
      const paymentReference = checkoutDetail?.payment?.reference;
      const response = await checkoutService.confirmCheckout(stayId, {
        idempotencyKey,
        paymentMethod,
        paymentReference,
        currency: paymentCurrency,
      });
      const checkoutRecord = response as any;
      navigate('/checkout/' + (checkoutRecord.id || checkoutRecord.checkout?.id) + '/complete');
    } catch (err: any) {
      console.error('Error confirming checkout:', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message;
      setError(typeof msg === 'string' ? msg : 'Erreur lors de la clôture');
      setShowConfirmModal(false);
    } finally {
      setConfirming(false);
    }
  };

  const isMissing = (value: any): boolean => value === undefined || value === null || value === '';

  const getValidationChecks = (): ValidationCheck[] => {
    if (validationResult?.checks) return validationResult.checks;
    return checkoutDetail?.validationChecks || [];
  };

  const allValidationsPassed = (): boolean => {
    const checks = getValidationChecks();
    return checks.length > 0 && checks.every((check) => check.passed);
  };

  const canCheckout = checkoutDetail?.canCheckout || allValidationsPassed();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error && !checkoutDetail) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow p-6 max-w-md">
          <div className="flex items-center text-red-600 mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
            <span className="font-medium">Erreur</span>
          </div>
          <p className="text-gray-700 mb-4">{error}</p>
          <div className="flex gap-3">
            <button onClick={() => navigate('/checkout')} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Retour à la liste</button>
            <button onClick={loadCheckoutDetail} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700">Réessayer</button>
          </div>
        </div>
      </div>
    );
  }

  if (!checkoutDetail) return null;

  const { guest, stay, billing, fpt, invoice, payment, discrepancy } = checkoutDetail;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button onClick={() => navigate('/checkout')} className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour à la liste
          </button>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Check-out</h1>
              <p className="text-gray-600 mt-1">{guest?.fullName || 'Client'} - Chambre {stay?.roomNumber || '-'}</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <button onClick={handleValidate} disabled={validating} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                {validating ? (<><ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />Validation...</>) : (<><CheckCircleIcon className="h-5 w-5 mr-2" />Valider</>)}
              </button>
              <button onClick={() => setShowConfirmModal(true)} disabled={!canCheckout} className={'inline-flex items-center px-4 py-2 rounded-md text-white ' + (canCheckout ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400 cursor-not-allowed')}>
                Clôturer le check-out
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Discrepancy Alert Banner */}
        {discrepancy && discrepancy.type !== 'match' && (
          <div className={`rounded-lg p-4 mb-6 border ${
            discrepancy.type === 'discount'
              ? 'bg-yellow-50 border-yellow-300'
              : 'bg-red-50 border-red-300'
          }`}>
            <div className="flex items-start">
              <ExclamationCircleIcon className={`h-6 w-6 mr-3 flex-shrink-0 ${
                discrepancy.type === 'discount' ? 'text-yellow-600' : 'text-red-600'
              }`} />
              <div>
                <h3 className={`font-semibold ${
                  discrepancy.type === 'discount' ? 'text-yellow-800' : 'text-red-800'
                }`}>
                  {discrepancy.type === 'discount'
                    ? 'Remise détectée'
                    : 'Surplus tarifaire détecté'}
                </h3>
                <p className={`text-sm mt-1 ${
                  discrepancy.type === 'discount' ? 'text-yellow-700' : 'text-red-700'
                }`}>
                  Montant theorique : {formatCurrency(discrepancy.theoreticalAmount, billing?.currency)} |
                  Montant facture : {formatCurrency(discrepancy.invoiceAmount, billing?.currency)} |
                  Ecart : {formatCurrency(Math.abs(discrepancy.difference), billing?.currency)} ({discrepancy.percentage.toFixed(1)}%)
                </p>
                {discrepancy.type === 'surplus' && (
                  <p className="text-sm mt-1 text-red-600 font-medium">
                    Ce surplus necessite une vérification administrative.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* Guest Info Card - Pre-filled from check-in */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-purple-600 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900">Informations Client</h2>
                  <span className="ml-auto text-xs text-green-600 bg-green-50 px-2 py-1 rounded">Pré-rempli</span>
                </div>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Nom complet</label>
                    <p className="mt-1 text-sm text-gray-900">{guest?.fullName || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Nationalité</label>
                    <p className={'mt-1 text-sm ' + (isMissing(guest?.nationality) ? 'text-red-600' : 'text-gray-900')}>{guest?.nationality || <span className="italic">Non renseignée</span>}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Type de document</label>
                    <p className={'mt-1 text-sm ' + (isMissing(guest?.documentType) ? 'text-red-600' : 'text-gray-900')}>{guest?.documentType || <span className="italic">Non renseigné</span>}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Numero de document</label>
                    <p className={'mt-1 text-sm ' + (isMissing(guest?.documentNumber) ? 'text-red-600' : 'text-gray-900')}>{guest?.documentNumber || <span className="italic">Non renseigné</span>}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stay Info Card - Pre-filled with editable departure */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <CalendarDaysIcon className="h-5 w-5 text-purple-600 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900">Informations Séjour</h2>
                  <span className="ml-auto text-xs text-green-600 bg-green-50 px-2 py-1 rounded">Pré-rempli</span>
                </div>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Chambre</label>
                    <p className="mt-1 text-sm text-gray-900">{stay?.roomNumber || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Type de chambre</label>
                    <p className="mt-1 text-sm text-gray-900">{stay?.roomType || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Date d'arrivee</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(stay?.checkinAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Nombre de nuits</label>
                    <p className={'mt-1 text-sm ' + (isMissing(stay?.nightsCount) ? 'text-red-600' : 'text-gray-900')}>{stay?.nightsCount != null ? stay.nightsCount + ' nuit' + (stay.nightsCount > 1 ? 's' : '') : <span className="italic">Non calculé</span>}</p>
                  </div>
                </div>

                {/* Editable departure fields */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Informations de départ</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="departureDate" className="block text-sm font-medium text-gray-700">
                        Date de départ <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="departureDate"
                        type="date"
                        value={departureDate}
                        onChange={(e) => setDepartureDate(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="departureMode" className="block text-sm font-medium text-gray-700">Mode de départ</label>
                      <select
                        id="departureMode"
                        value={departureMode}
                        onChange={(e) => setDepartureMode(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      >
                        {DEPARTURE_MODES.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleUpdateDeparture}
                    disabled={updatingDeparture || !departureDate}
                    className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 disabled:opacity-50"
                  >
                    {updatingDeparture ? (
                      <><ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />Mise à jour...</>
                    ) : (
                      'Mettre à jour le départ'
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Billing Card - Auto-calculated with rate info */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-purple-600 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900">Facturation</h2>
                  {billing?.ratePerNight ? (
                    <span className="ml-auto text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">Auto-calculé</span>
                  ) : null}
                </div>
              </div>
              <div className="px-6 py-4 space-y-4">
                {billing?.ratePerNight ? (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm">
                    <p className="text-blue-800">
                      <span className="font-medium">Tarif configuré :</span> {formatCurrency(billing.ratePerNight, billing?.currency)} / nuit x {stay?.nightsCount || 0} nuits = {formatCurrency(billing.theoreticalAmount, billing?.currency)}
                    </p>
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Hébergement</label>
                    <p className={'mt-1 text-sm ' + (isMissing(billing?.accommodationAmount) ? 'text-red-600' : 'text-gray-900')}>{formatCurrency(billing?.accommodationAmount, billing?.currency)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Extras</label>
                    <p className="mt-1 text-sm text-gray-900">{formatCurrency(billing?.extrasAmount || 0, billing?.currency)}</p>
                  </div>
                </div>

                {/* Invoice amount input for comparison */}
                <div className="pt-3 border-t border-gray-200">
                  <label htmlFor="invoiceAmount" className="block text-sm font-medium text-gray-700">
                    Montant facture (pour comparaison)
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      id="invoiceAmount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoiceAmount}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setInvoiceAmount(isNaN(val) || val < 0 ? '' : e.target.value);
                      }}
                      placeholder="Saisir le montant de la facture"
                      className="flex-1 min-w-0 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    />
                  </div>
                  {invoiceAmount && billing?.theoreticalAmount ? (() => {
                    const inv = parseFloat(invoiceAmount);
                    const theo = billing.theoreticalAmount;
                    const diff = inv - theo;
                    const pct = Math.abs(diff) / theo * 100;
                    if (pct < 0.5) return null;
                    const isDiscount = diff < 0;
                    return (
                      <div className={`mt-2 p-2 rounded text-sm ${isDiscount ? 'bg-yellow-50 text-yellow-800' : 'bg-red-50 text-red-800'}`}>
                        {isDiscount
                          ? `Remise de ${formatCurrency(Math.abs(diff), billing?.currency)} (${pct.toFixed(1)}%) par rapport au tarif configure`
                          : `Surplus de ${formatCurrency(diff, billing?.currency)} (${pct.toFixed(1)}%) - vérification administrative requise`}
                      </div>
                    );
                  })() : null}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Total</label>
                    <p className={'text-lg font-semibold ' + (isMissing(billing?.totalAmount) ? 'text-red-600' : 'text-gray-900')}>{formatCurrency(billing?.totalAmount, billing?.currency)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* FPT Card */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-purple-600 mr-2" />
                  <h2 className="text-lg font-medium text-gray-900">Calcul FPT</h2>
                  <span className="ml-auto text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">Auto-calculé</span>
                </div>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Taux FPT</label>
                    <p className={'mt-1 text-sm ' + (isMissing(fpt?.rate) ? 'text-red-600' : 'text-gray-900')}>{fpt?.rate != null ? (fpt.rate * 100).toFixed(0) + '%' : <span className="italic">Non configuré</span>}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Base taxable</label>
                    <p className={'mt-1 text-sm ' + (isMissing(fpt?.taxableAmount) ? 'text-red-600' : 'text-gray-900')}>{formatCurrency(fpt?.taxableAmount, billing?.currency)}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Montant FPT</label>
                    <p className={'text-lg font-semibold ' + (isMissing(fpt?.amount) ? 'text-red-600' : 'text-purple-600')}>{formatCurrency(fpt?.amount, billing?.currency)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Card */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentArrowDownIcon className="h-5 w-5 text-purple-600 mr-2" />
                    <h2 className="text-lg font-medium text-gray-900">Facture</h2>
                  </div>
                  {!invoice?.fileUrl && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                      <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                      Obligatoire
                    </span>
                  )}
                </div>
              </div>
              <div className="px-6 py-4">
                {invoice?.fileUrl ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-8 w-8 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{invoice?.fileName || 'Facture'}</p>
                          <p className="text-xs text-gray-500">Téléversée le {formatDateTime(invoice?.uploadedAt)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a href={invoice?.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                          <EyeIcon className="h-4 w-4 mr-1" />Voir
                        </a>
                        <a href={invoice?.fileUrl} download={invoice?.fileName || 'facture'} className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                          <DocumentArrowDownIcon className="h-4 w-4 mr-1" />Telecharger
                        </a>
                      </div>
                    </div>
                    <div>
                      <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect} className="hidden" id="invoice-replace" />
                      <label htmlFor="invoice-replace" className={'inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer ' + (uploading ? 'opacity-50 cursor-not-allowed' : '')}>
                        {uploading ? (<><ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />Téléversement...</>) : (<><ArrowUpTrayIcon className="h-4 w-4 mr-1" />Remplacer</>)}
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-4">Aucune facture téléversée</p>
                    <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect} className="hidden" id="invoice-upload" />
                    <label htmlFor="invoice-upload" className={'inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 cursor-pointer ' + (uploading ? 'opacity-50 cursor-not-allowed' : '')}>
                      {uploading ? (<><ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />Téléversement...</>) : (<><ArrowUpTrayIcon className="h-5 w-5 mr-2" />Téléverser une facture</>)}
                    </label>
                    <p className="text-xs text-gray-500 mt-2">Formats acceptés: PDF, JPG, JPEG, PNG (max 10 Mo)</p>
                  </div>
                )}
                {uploadError && (<div className="mt-3 p-3 bg-red-50 rounded-lg"><p className="text-sm text-red-800">{uploadError}</p></div>)}
              </div>
            </div>

            {/* Payment Card */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CreditCardIcon className="h-5 w-5 text-purple-600 mr-2" />
                    <h2 className="text-lg font-medium text-gray-900">Paiement</h2>
                  </div>
                  {!payment?.method && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                      <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                      Obligatoire
                    </span>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Méthode</label>
                    <p className={'mt-1 text-sm ' + (isMissing(payment?.method) ? 'text-red-600' : 'text-gray-900')}>{payment?.method || <span className="italic">Non renseignée</span>}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Reference</label>
                    <p className="mt-1 text-sm text-gray-900">{payment?.reference || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Statut</label>
                    <p className="mt-1">
                      {payment?.status ? (
                        <span className={'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ' + (payment.status.toLowerCase() === 'paid' || payment.status.toLowerCase() === 'paye' ? 'bg-green-100 text-green-800' : payment.status.toLowerCase() === 'pending' || payment.status.toLowerCase() === 'en attente' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800')}>{payment.status}</span>
                      ) : (<span className="text-sm text-gray-500 italic">Non renseigné</span>)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Montant paye</label>
                    <p className="mt-1 text-sm text-gray-900">{payment?.amount != null ? formatCurrency(payment.amount, billing?.currency) : '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Validation Checks Card */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-purple-600 mr-2" />
                    <h2 className="text-lg font-medium text-gray-900">Contrôles de Conformité</h2>
                  </div>
                  {getValidationChecks().length > 0 && (
                    <span className={'inline-flex items-center px-2 py-1 rounded text-xs font-medium ' + (allValidationsPassed() ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')}>
                      {allValidationsPassed() ? (<><CheckCircleIcon className="h-4 w-4 mr-1" />Tous valides</>) : (<><XCircleIcon className="h-4 w-4 mr-1" />{getValidationChecks().filter((c) => !c.passed).length} erreur(s)</>)}
                    </span>
                  )}
                </div>
              </div>
              <div className="px-6 py-4">
                {getValidationChecks().length > 0 ? (
                  <ul className="space-y-3">
                    {getValidationChecks().map((check) => (
                      <li key={check.id} className="flex items-start">
                        {check.passed ? (<CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />) : (<XCircleIcon className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />)}
                        <div>
                          <p className={'text-sm ' + (check.passed ? 'text-gray-900' : 'text-red-800 font-medium')}>{check.label}</p>
                          {!check.passed && check.errorMessage && (<p className="text-xs text-red-600 mt-1">{check.errorMessage}</p>)}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (<div className="text-center py-4"><p className="text-sm text-gray-500">Cliquez sur Valider pour verifier les controles</p></div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Confirm Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => !confirming && setShowConfirmModal(false)}></div>
              <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
                <div className="flex items-center mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mr-3" />
                  <h3 className="text-lg font-medium text-gray-900">Confirmer la clôture du check-out</h3>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Récapitulatif</h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between"><dt className="text-gray-500">Client</dt><dd className="text-gray-900 font-medium">{guest?.fullName || '-'}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500">Chambre</dt><dd className="text-gray-900">{stay?.roomNumber || '-'} {stay?.roomType ? `(${stay.roomType})` : ''}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500">Dates</dt><dd className="text-gray-900">{formatDate(stay?.checkinAt)} - {formatDate(stay?.checkoutAt)}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500">Nuits</dt><dd className="text-gray-900">{stay?.nightsCount || '-'}</dd></div>
                    {billing?.ratePerNight ? (
                      <div className="flex justify-between"><dt className="text-gray-500">Tarif / nuit</dt><dd className="text-gray-900">{formatCurrency(billing.ratePerNight, billing?.currency)}</dd></div>
                    ) : null}
                    <div className="flex justify-between border-t border-gray-200 pt-2 mt-2"><dt className="text-gray-700 font-medium">Total</dt><dd className="text-gray-900 font-semibold">{formatCurrency(billing?.totalAmount, paymentCurrency)}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500">FPT</dt><dd className="text-purple-600 font-medium">{formatCurrency(fpt?.amount, paymentCurrency)}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500">Paiement</dt><dd className="text-gray-900">{payment?.method || '-'}</dd></div>
                    <div className="flex items-center justify-between gap-4 pt-2">
                      <dt className="text-gray-500">Devise payée</dt>
                      <dd>
                        <select
                          value={paymentCurrency}
                          onChange={(e) => setPaymentCurrency(e.target.value === 'CDF' ? 'CDF' : 'USD')}
                          disabled={confirming}
                          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-50"
                        >
                          <option value="USD">USD</option>
                          <option value="CDF">CDF</option>
                        </select>
                      </dd>
                    </div>
                  </dl>
                </div>
                {discrepancy && discrepancy.type !== 'match' && (
                  <div className={`p-3 rounded-lg mb-4 ${discrepancy.type === 'discount' ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`text-sm font-medium ${discrepancy.type === 'discount' ? 'text-yellow-800' : 'text-red-800'}`}>
                      {discrepancy.type === 'discount' ? 'Remise' : 'Surplus'} de {formatCurrency(Math.abs(discrepancy.difference), billing?.currency)} ({discrepancy.percentage.toFixed(1)}%)
                    </p>
                  </div>
                )}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800"><strong>Attention :</strong> Cette action est irreversible. Une fois clôturé, le dossier sera verrouillé et ne pourra plus être modifié.</p>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setShowConfirmModal(false)} disabled={confirming} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Annuler</button>
                  <button type="button" onClick={handleConfirmCheckout} disabled={confirming} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50">
                    {confirming ? (<><ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />Clôture en cours...</>) : 'Confirmer la clôture'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
